import React, { useEffect, useState } from "react";

import { Box, Label, Stack, TableLoader } from "codekit";

import { useLazyGetMipuestoMiDiaQuery, useLazyGetMipuestoWidgetsQuery } from "../../libs/redux/slices/Mipuesto/mipuestoApiSlice";
import { useNavigateProcess } from "../../utils/hooks/useNavigateProcess";
import { tono } from "../Tablerox/tonos";

import Miescrix from "./Miescrix";

import "../Tablerox/riodo.css";
import "./midia.css";

//Tono RIO de la franja: comunica urgencia sin leer.
const tonoFranja = {
  DESTRABA: "ROJO",
  RIESGO: "NARANJA",
  ASIGNADAS: "VERDE",
  HEREDADO: "ROJO",
  COLA: "AZUL",
  DOCUMENTAL: "GRIS",
};

//Convierte los filtros planos del backend {CAMPO: valor} al formato de
//filtros inducidos del tracking [{CAMPO: {IN: [valor]}}]
const filtrosInducidos = (filtros) =>
  Object.entries(filtros ?? {}).map(([campo, valor]) => ({ [campo]: { IN: [valor] } }));

/**
 * Mi día: qué debo hacer hoy y en qué orden.
 *
 * La primera pantalla de Mi Puesto (FASE 5, pieza 4). Una lista priorizada
 * por franjas — destrabar a otros, riesgo, mi cola, documental — donde
 * cada elemento dice POR QUÉ está ahí y lleva de un clic a donde se
 * resuelve. Las franjas se componen por permisos: las personales nacen de
 * mis asignaciones y las de pozo común solo aparecen si tengo el módulo
 * en mi menú. Una franja sin ítems no se pinta.
 *
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-08-11
 */
//Una franja de Mi día NO se repite si el escritorio del usuario ya trae el tablero que la cubre (Diego 2026-09-21: "ya
//está cubierto abajo"): "En riesgo" lo cubre Mis alertas de hoy (110) y "Tu cola" el Tablero de tareas (100). Las
//franjas sin tablero equivalente (destrabar a otros, pendientes de tus documentos, gestión documental) se siguen viendo.
const TABLERO_QUE_CUBRE = { RIESGO: 110, COLA: 100 };

//Las listas van plegadas detrás de los tiles (Diego 2026-09-24: con muchas operaciones asignadas la lista se
//comía toda la pantalla y tapaba el escritorio). Solo se abre la franja que se toca; de entrada, la primera.
const Midiaxxx = () => {
  const [franjasDelDia, setFranjas] = useState(null);
  const [tableros, setTableros] = useState([]);
  const [abierta, setAbierta] = useState(null);
  const [getMiDia] = useLazyGetMipuestoMiDiaQuery();
  const [getWidgets] = useLazyGetMipuestoWidgetsQuery();
  useEffect(() => {
    getWidgets().unwrap().then((response) => setTableros((response?.data?.widgets ?? []).map((w) => Number(w.GRAIDXXX)))).catch(() => setTableros([]));
  }, [getWidgets]);
  const franjas = franjasDelDia === null ? null : franjasDelDia.filter((franja) => !tableros.includes(TABLERO_QUE_CUBRE[franja.clave]));
  const { handleNavigateProcess } = useNavigateProcess();

  useEffect(() => {
    const handleCargar = async () => {
      await getMiDia()
        .unwrap()
        .then((response) => setFranjas(response?.data?.franjas ?? []))
        .catch(() => setFranjas([]));
    };
    handleCargar();
    const timer = setInterval(handleCargar, 60000);
    return () => clearInterval(timer);
  }, [getMiDia]);

  const handleIr = (item) => {
    const destino = item?.destino ?? {};
    if (!destino.modulo) {
      return;
    }
    handleNavigateProcess(destino.modulo, destino.proceso, filtrosInducidos(destino.filtros));
  };

  //null = aún sin decidir (se abre la primera franja VISIBLE); "" = el usuario la plegó
  useEffect(() => {
    if (abierta === null && franjas && franjas.length > 0) {
      setAbierta(franjas[0].clave);
    }
  }, [abierta, franjas]);

  const handleAbrir = (clave) => setAbierta((actual) => (actual === clave ? "" : clave));

  if (franjas === null) {
    return (
      <Box spacing={4}>
        <TableLoader />
      </Box>
    );
  }

  if (franjas.length === 0 && franjasDelDia.length > 0) {
    //Todo lo del día ya lo muestran los tableros del escritorio: no se repite nada arriba
    return <Miescrix />;
  }

  if (franjas.length === 0) {
    return (
      <Stack gap={4}>
        <Box spacing={6} rounded="md" bg="success-100">
          <Label color="success-700" weight="bold">
            Nada pendiente por hoy: no tienes autorizaciones esperando, operaciones en
            riesgo ni tareas abiertas.
          </Label>
        </Box>
        <Miescrix />
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      <div className="riodo-escala">
        <div className="riodo midia">
          {/* Tiles de resumen: el tamaño del día de un vistazo; cada tile abre o pliega su lista */}
          <section className="rio-strip midia-tiles" aria-label="Mi día: cada tarjeta abre su lista">
            {franjas.map((franja) => {
              const activa = abierta === franja.clave;
              return (
                <button
                  key={`tile-${franja.clave}`}
                  type="button"
                  className={`rio-card midia-tile${activa ? " activa" : ""}`}
                  style={{ "--tono": tono(tonoFranja[franja.clave] ?? "AZUL").hex }}
                  onClick={() => handleAbrir(franja.clave)}
                >
                  <span className="rio-kicker">{franja.titulo}</span>
                  <span className="midia-tile-valor">{franja.total}</span>
                  <span className="rio-strip-nota">
                    {franja.subtitulo || (activa ? "ocultar detalle" : "ver detalle")}
                  </span>
                </button>
              );
            })}
          </section>

          {franjas
            .filter((franja) => franja.clave === abierta)
            .map((franja) => {
              const estilo = tono(tonoFranja[franja.clave] ?? "AZUL");
              return (
                <section key={franja.clave} className="rio-card midia-lista">
                  <div className="midia-lista-cabeza">
                    <span className="rio-card-title">{franja.titulo}</span>
                    <span className="rio-chip" style={estilo.chip}>
                      {franja.subtitulo || `${franja.total} en total`}
                    </span>
                    {franja.total > franja.items.length && (
                      <span className="rio-kicker">mostrando {franja.items.length} de {franja.total}</span>
                    )}
                    <button
                      type="button"
                      className="rio-icon-btn"
                      title="Plegar"
                      style={{ marginLeft: franja.total > franja.items.length ? 0 : "auto" }}
                      onClick={() => handleAbrir(franja.clave)}
                    >
                      ▴
                    </button>
                  </div>
                  {franja.items.map((item, indice) => (
                    <div key={`${franja.clave}-${indice}`} className="midia-fila">
                      <div className="midia-fila-texto">
                        <span className="midia-fila-titulo">{item.titulo}</span>
                        <span className="midia-fila-razon">{item.razon}</span>
                      </div>
                      {Boolean(item.destino?.modulo) && (
                        <button type="button" className="midia-ir" onClick={() => handleIr(item)}>
                          Ir ›
                        </button>
                      )}
                    </div>
                  ))}
                </section>
              );
            })}
        </div>
      </div>

      <Miescrix />
    </Stack>
  );
};

export default Midiaxxx;
